import { siteConfig } from '@/config/site'
import { VideoType } from '@/constants/video'
import { camelizeKeys, snakelizeKeys } from '@/lib/utils'
import { PageInfo, PaginationData } from '@/types/common'
import { Video, VideoComment } from '@/types/video'
import { PurchaseVideoResponse } from '@/types/video'
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import { omit, pathOr } from 'ramda'

import { axiosInstance } from '../axios'

export const fetchVideos = async ({
  params,
}: {
  params: Record<string, string | number | boolean | Array<number>>
}) => {
  try {
    // const categoryIds = [1, 2, 3, 15, 17];
    // const old = `categoryIds[0]=1&categoryIds[1]=2&categoryIds[2]=3&categoryIds[3]=15&categoryIds[4]=17`
    // const new = `categoryId=1&categoryId=2&categoryId=3&categoryId=15&categoryId=17`

    const config = snakelizeKeys({
      params: omit(
        [
          'sexualCategoryIds',
          'categoryIds',
          'channelIds',
          'showSexualTagsOnly',
          'userId',
          'videoIds',
        ],
        params,
      ),
    })

    let sexualCategoryIdsString = ''
    let categoryIdsString = ''
    let channelIdsString = ''
    let videoIdsString = ''
    let url = '/videos'

    const sexualCategoryIds = pathOr([], ['sexualCategoryIds'], params)
    const categoryIds = pathOr([], ['categoryIds'], params)
    const channelIds = pathOr([], ['channelIds'], params)

    const videoIds = pathOr([], ['videoIds'], params)

    const hasSexualCategories = sexualCategoryIds.length > 0
    const hasCategories = categoryIds.length > 0
    const hasChannelIds = channelIds.length > 0
    const hasVideoIds = videoIds.length > 0

    if (hasSexualCategories) {
      const selectedIds = sexualCategoryIds?.filter((id: number) => {
        return siteConfig.sexualPreferenceCategoryIds.includes(id)
      })
      sexualCategoryIdsString = selectedIds
        // @ts-ignore
        .map((id: number) => `sexual_category_id=${id}`)
        .join('&')
    }

    if (hasCategories) {
      const selectedIds =
        pathOr(false, ['showSexualTagsOnly'], params) && hasCategories
          ? categoryIds?.filter((id: number) => {
              return siteConfig.sexualPreferenceCategoryIds.includes(id)
            })
          : params.categoryIds
      categoryIdsString = selectedIds
        // @ts-ignore
        .map((id: number) => `category_id=${id}`)
        .join('&')
    }

    if (hasChannelIds) {
      channelIdsString = channelIds
        // @ts-ignore
        .map((id: number) => `channel_id=${id}`)
        .join('&')
    }

    if (hasVideoIds) {
      videoIdsString = videoIds
        // @ts-ignore
        .map((id: number) => `video_id=${id}`)
        .join('&')
    }

    const query = [
      sexualCategoryIdsString,
      categoryIdsString,
      channelIdsString,
      videoIdsString,
    ]
      .filter(str => str.length > 0)
      .join('&')
    if (query.length > 0) {
      url = `/videos?${query}`
    }

    // @ts-ignore
    const response = await axiosInstance.get<any, RawListResponse<Video>>(
      url,
      config,
    )
    return camelizeKeys(response) as PaginationData<Video>
  } catch (error) {
    console.error('🚀 ~ error:', error)
  }
}

const fetchVideo = async ({ id }: { id: number }) => {
  const resData = await axiosInstance.get<any, Video>(`/videos/${id}`)
  return camelizeKeys(resData)
}

export type VideoCommentsResponse = {
  list: VideoComment[]
  pagination: PageInfo
}

const fetchVideoComments = async ({
  videoId,
}: {
  videoId: number
}): Promise<VideoCommentsResponse> => {
  const resData = await axiosInstance.get<any, VideoCommentsResponse>(
    `/videos/${videoId}/comments`,
    { params: { limit: 10 } },
  )
  // @ts-ignore
  return camelizeKeys(resData)
}

// NOTE: https://tinyurl.com/2xv885ny
const fetchVideoCommentReplies = async ({
  videoId,
  commentId,
}: {
  videoId: number
  commentId: string
}): Promise<VideoCommentsResponse> => {
  const resData = await axiosInstance.get<any, VideoCommentsResponse>(
    `/videos/${videoId}/comments/${commentId}/replies`,
  )
  // @ts-ignore
  return camelizeKeys(resData)
}
export function useVideos({
  params,
}: {
  params?: Record<string, string | boolean | number[] | number>
}) {
  return useInfiniteQuery({
    queryKey: ['videos', params, params?.userId],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const resData = await fetchVideos({
        params: {
          ...params,
          page: pageParam,
        },
      })
      return resData
    },
    retry: false,
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined
      // 如果當前頁是最後一頁，則返回 undefined
      // @ts-ignore
      return lastPage.pagination.currentPage < lastPage?.pagination?.totalPages
        ? // @ts-ignore
          lastPage.pagination.nextPage
        : undefined
    },
  })
}

interface EventVideosPayload {
  params?: Record<string, any>
  enabled?: boolean
}
export function useEventVideos({ params, enabled = true }: EventVideosPayload) {
  return useInfiniteQuery({
    queryKey: [
      'videos',
      'event',
      params?.userId,
      params?.sexualCategoryIds,
      params?.pageSize,
      params?.categoryIds,
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      // @ts-ignore
      const resData = await fetchVideos({
        params: {
          ...params,
          type: VideoType.Event,
          isReviewed: true,
          sortField: 'views',
          sortOrder: 'DESC',
          // showSexualTagsOnly: true,
          page: pageParam,
        },
      })
      return resData
    },
    retry: false,
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined
      // 如果當前頁是最後一頁，則返回 undefined
      // @ts-ignore
      return lastPage.pagination.currentPage < lastPage?.pagination?.totalPages
        ? // @ts-ignore
          lastPage.pagination.nextPage
        : undefined
    },
    enabled,
  })
}

// NOTE: MFANS STAR
interface StarVideosPayload {
  params?: Record<string, any>
  enabled?: boolean
}
export function useStarVideos({ params, enabled = true }: StarVideosPayload) {
  return useInfiniteQuery({
    queryKey: [
      'videos',
      'star',
      params?.userId,
      params?.sexualCategoryIds,
      params?.pageSize,
      params?.videoIds,
      params?.channelIds,
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      // @ts-ignore
      const omittedParams = omit(['sexualCategoryIds'], params)
      // @ts-ignore
      const resData = await fetchVideos({
        params: {
          ...omittedParams,
          // NOTE: MFANS STAR 影片不另外做排序
          // sortField: 'views',
          // sortOrder: 'DESC',
          isReviewed: true,
          page: pageParam,
        },
      })
      return resData
    },
    retry: false,
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined
      // 如果當前頁是最後一頁，則返回 undefined
      // @ts-ignore
      return lastPage.pagination.currentPage < lastPage?.pagination?.totalPages
        ? // @ts-ignore
          lastPage.pagination.nextPage
        : undefined
    },
    enabled,
  })
}

// NOTE: MFANS 精選
interface FeaturedVideosPayload {
  params?: Record<string, any>
  enabled?: boolean
}
export function useFeaturedVideos({
  params,
  enabled = true,
}: FeaturedVideosPayload) {
  return useInfiniteQuery({
    queryKey: [
      'videos',
      'featured',
      params?.userId,
      params?.categoryIds,
      params?.pageSize,
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      // @ts-ignore
      const resData = await fetchVideos({
        params: {
          ...params,
          sortField: 'views',
          sortOrder: 'DESC',
          isReviewed: true,
          showSexualTagsOnly: true,
          page: pageParam,
        },
      })
      return resData
    },
    retry: false,
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined
      // 如果當前頁是最後一頁，則返回 undefined
      // @ts-ignore
      return lastPage.pagination.currentPage < lastPage?.pagination?.totalPages
        ? // @ts-ignore
          lastPage.pagination.nextPage
        : undefined
    },
    enabled,
  })
}

interface NewVideosPayload {
  params?: Record<string, any>
  enabled?: boolean
}

export function useNewVideos({ params, enabled = true }: NewVideosPayload) {
  return useInfiniteQuery({
    queryKey: [
      'videos',
      'new',
      params?.userId,
      params?.sexualCategoryIds,
      params?.pageSize,
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      // @ts-ignore
      const resData = await fetchVideos({
        params: {
          ...params,
          isReviewed: true,
          sortOrder: 'DESC',
          page: pageParam,
        },
      })

      return resData
    },
    retry: false,
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined
      // 如果當前頁是最後一頁，則返回 undefined
      // @ts-ignore
      return lastPage.pagination.currentPage < lastPage?.pagination?.totalPages
        ? // @ts-ignore
          lastPage.pagination.nextPage
        : undefined
    },
    enabled,
  })
}

interface FreeVideosPayload {
  params?: Record<string, any>
  enabled?: boolean
}
export function useFreeVideos({ params, enabled = true }: FreeVideosPayload) {
  return useInfiniteQuery({
    queryKey: [
      'videos',
      'free',
      params?.userId,
      params?.categoryIds,
      params?.pageSize,
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      // @ts-ignore
      const resData = await fetchVideos({
        params: {
          ...params,
          type: VideoType.Free,
          isReviewed: true,
          sortField: 'views',
          sortOrder: 'DESC',
          showSexualTagsOnly: true,
          page: pageParam,
        },
      })
      return resData
    },
    retry: false,
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined
      // 如果當前頁是最後一頁，則返回 undefined
      // @ts-ignore
      return lastPage.pagination.currentPage < lastPage?.pagination?.totalPages
        ? // @ts-ignore
          lastPage.pagination.nextPage
        : undefined
    },
    enabled,
  })
}
interface PopularVideosPayload {
  params?: Record<string, any>
  enabled?: boolean
}
export function usePopularVideos({
  params,
  enabled = true,
}: PopularVideosPayload) {
  return useInfiniteQuery({
    queryKey: [
      'videos',
      'popular',
      params?.userId,
      params?.categoryIds,
      params?.pageSize,
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      // @ts-ignore
      const resData = await fetchVideos({
        params: {
          ...params,
          sortField: 'views',
          sortOrder: 'DESC',
          isReviewed: true,
          showSexualTagsOnly: true,
          page: pageParam,
        },
      })

      return resData
    },
    retry: false,
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined
      // 如果當前頁是最後一頁，則返回 undefined
      // @ts-ignore
      return lastPage.pagination.currentPage < lastPage?.pagination?.totalPages
        ? // @ts-ignore
          lastPage.pagination.nextPage
        : undefined
    },
    enabled,
  })
}

export function useGuessYouLikeVideos({
  params,
  enabled = true,
}: PopularVideosPayload) {
  return useInfiniteQuery({
    queryKey: [
      'videos',
      'guess-you-like',
      params?.userId,
      params?.categoryIds,
      params?.pageSize,
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      // @ts-ignore
      const resData = await fetchVideos({
        params: {
          ...params,
          isReviewed: true,
          page: pageParam,
        },
      })
      return resData
    },
    retry: false,
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined
      // 如果當前頁是最後一頁，則返回 undefined
      // @ts-ignore
      return lastPage.pagination.currentPage < lastPage?.pagination?.totalPages
        ? // @ts-ignore
          lastPage.pagination.nextPage
        : undefined
    },
    enabled,
  })
}

interface OtherVideosPayload {
  params?: Record<string, any>
}

export function useChannelVideos({ params }: OtherVideosPayload) {
  return useInfiniteQuery({
    queryKey: ['videos', 'other', params?.channelId, params?.userId],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      // @ts-ignore
      const resData = await fetchVideos({
        params: {
          ...params,
          page: pageParam,
        },
      })
      return resData
    },
    retry: false,
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined
      // 如果當前頁是最後一頁，則返回 undefined
      // @ts-ignore
      return lastPage.pagination.currentPage < lastPage?.pagination?.totalPages
        ? // @ts-ignore
          lastPage.pagination.nextPage
        : undefined
    },
    enabled: !!params?.channelId,
  })
}

export function useRecommendVideos({ params }: OtherVideosPayload) {
  return useInfiniteQuery({
    queryKey: [
      'videos',
      'recommend',
      params?.userId,
      params?.channelIds,
      params?.pageSize,
      params?.categoryIds,
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      // @ts-ignore
      const resData = await fetchVideos({
        params: {
          ...params,
          page: pageParam,
          sortField: 'views',
          sortOrder: 'DESC',
          isReviewed: true,
        },
      })
      return resData
    },
    retry: false,
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined
      // 如果當前頁是最後一頁，則返回 undefined
      // @ts-ignore
      return lastPage.pagination.currentPage < lastPage?.pagination?.totalPages
        ? // @ts-ignore
          lastPage.pagination.nextPage
        : undefined
    },
    enabled: !!params?.categoryIds && !!params?.channelIds,
  })
}

export function useVideo({ id }: { id: number }) {
  return useQuery({
    queryKey: ['video', id],
    queryFn: () => fetchVideo({ id }),
  })
}

export function useVideoComments({ videoId }: { videoId: number }) {
  return useQuery({
    queryKey: ['video', videoId, 'comments'],
    queryFn: () =>
      fetchVideoComments({
        videoId,
      }),
    retry: false,
  })
}

export function useVideoCommentReplies({
  videoId,
  commentId,
}: {
  videoId: number
  commentId: string
}) {
  return useQuery({
    queryKey: ['video', videoId, 'comment', commentId, 'replies'],
    queryFn: () =>
      fetchVideoCommentReplies({
        videoId,
        commentId,
      }),
    retry: false,
    enabled: !!commentId,
  })
}

// ** 更新影片觀看次數
export function useUpdateVideoViews({ videoId }: { videoId: number }) {
  return useMutation({
    mutationFn: async (): Promise<null> => {
      const { data } = await axiosInstance.put(`/videos/${videoId}/view`)
      return camelizeKeys(data)
    },
  })
}

// ** 更新影片資訊
export function useUpdateVideo(channelId: number) {
  return useMutation({
    mutationFn: async (formData: Video): Promise<null> => {
      const body = snakelizeKeys(formData)
      const { data } = await axiosInstance.put(
        `/channels/${channelId}/videos/${body.id}`,
        body,
      )
      return camelizeKeys(data)
    },
  })
}

// ** 刪除作品
export function useDeleteVideo(channelId: number) {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await axiosInstance.delete(
        `/channels/${channelId}/videos/${id}`,
      )
      return camelizeKeys(data)
    },
  })
}

// ** 單一影片詳請
export function useFetchVideo(channelId: number) {
  return useMutation({
    mutationFn: async (id: string | number) => {
      const data = await axiosInstance.get(
        `/channels/${channelId}/videos/${id}`,
      )

      return camelizeKeys(data)
    },
  })
}

// ** 喜歡影片 ON
export function useLikeVideo(videoId: number) {
  return useMutation({
    mutationFn: () => axiosInstance.post(`/videos/${videoId}/like`, {}),
  })
}

// ** 喜歡影片 OFF
export function useDislikeVideo(videoId: number) {
  return useMutation({
    mutationFn: () =>
      axiosInstance.delete(`/videos/${videoId}/like`, {
        data: {},
      }),
  })
}

// ** 收藏影片
export function useFavorite() {
  return useMutation({
    mutationFn: ({ id, type }: { id: number; type: string }) =>
      axiosInstance.patch(`/user/favorite`, {
        id,
        type,
      }),
  })
}

// ** like 留言 ON
export function useCommentsLikeOn(videoId: number) {
  return useMutation({
    mutationFn: (commentId: number) =>
      axiosInstance.post(`/videos/${videoId}/comments/${commentId}/like`, {}),
  })
}

// ** like 留言 OFF
export function useCommentsLikeOff(videoId: number) {
  return useMutation({
    mutationFn: (commentId: number) =>
      axiosInstance.delete(`/videos/${videoId}/comments/${commentId}/like`, {
        data: {},
      }),
  })
}

// ** like 回覆 ON
export function useCommentsRepliesLikeOn(videoId: number) {
  return useMutation({
    mutationFn: ({
      commentId,
      replyId,
    }: {
      commentId: number
      replyId: number
    }) =>
      axiosInstance.post(
        `/videos/${videoId}/comments/${commentId}/replies/${replyId}/like`,
        {},
      ),
  })
}

// ** like 回覆 OFF
export function useCommentsRepliesLikeOff(videoId: number) {
  return useMutation({
    mutationFn: ({
      commentId,
      replyId,
    }: {
      commentId: number
      replyId: number
    }) =>
      axiosInstance.delete(
        `/videos/${videoId}/comments/${commentId}/replies/${replyId}/like`,
        {
          data: {},
        },
      ),
  })
}

// ** 黑名單 ON
export function useUserBlacklistOn() {
  return useMutation({
    mutationFn: (userId: number) => {
      const payload = snakelizeKeys({
        blockedId: userId,
        reason: '',
      })

      return axiosInstance.post(`/user/blacklist`, payload)
    },
  })
}

// ** 黑名單 OFF
export function useUserBlacklistOff() {
  return useMutation({
    mutationFn: (userId: number) =>
      axiosInstance.delete(`/user/blacklist/${userId}`, {
        data: {},
      }),
  })
}

// ** 更新屏蔽影片留言
export function useShieldVideoComments(channelId: number) {
  return useMutation({
    mutationFn: ({
      videoId,
      commentId,
    }: {
      videoId: number
      commentId: number
    }) => {
      const body = snakelizeKeys({ isHidden: true })

      return axiosInstance.put(
        `/channels/${channelId}/videos/${videoId}/comments/${commentId}`,
        body,
      )
    },
  })
}

// ** 更新屏蔽影片留言回覆
export function useShieldVideoCommentsReply(channelId: number) {
  return useMutation({
    mutationFn: ({
      videoId,
      commentId,
      replyId,
    }: {
      videoId: number
      commentId: number
      replyId: number
    }) => {
      const body = snakelizeKeys({ isHidden: true })

      return axiosInstance.put(
        `/channels/${channelId}/videos/${videoId}/comments/${commentId}/replies/${replyId}`,
        body,
      )
    },
  })
}

type PurchaseVideoPayload = {
  videoId: string
  payType: string
  returnUrl: string
  promoCode?: string
}
export function usePurchaseVideo() {
  return useMutation({
    mutationFn: ({
      videoId,
      payType,
      returnUrl,
      promoCode,
    }: PurchaseVideoPayload) => {
      const body = snakelizeKeys({
        videoId: parseInt(videoId, 10),
        payType,
        returnUrl,
        promoCode,
      })

      return axiosInstance.post<any, PurchaseVideoResponse>(
        `/purchase/video`,
        body,
      )
    },
  })
}

export function useWatermarkSetting(channelId: number) {
  return useMutation({
    mutationFn: ({ videoWatermark }: { videoWatermark: boolean }) => {
      const body = snakelizeKeys({ videoWatermark })

      return axiosInstance.put(`/channels/${channelId}/setting`, body)
    },
  })
}

interface VideoPaymentsPayload {
  videoId: number
  promoCode?: string
}

export function useVideoPayments(payload: VideoPaymentsPayload) {
  const { videoId, promoCode } = payload
  return useQuery({
    queryKey: ['video-payments', videoId],
    queryFn: () =>
      axiosInstance.get(`/purchase/payChannel/video`, {
        params: {
          ...(promoCode && { promo_code: promoCode }),
          video_id: videoId,
        },
      }),
    enabled: !!videoId,
  })
}
